type BookingRuleCourseNode = {
  id?: string | null;
  name?: string | null;
};

type BookingRuleCourseEdge =
  | {
      node?: BookingRuleCourseNode | null;
    }
  | null
  | undefined;

type BookingRuleCoursesConnection = {
  count?: number | null;
  edges?: readonly BookingRuleCourseEdge[] | null;
} | null;

type BookingRuleWindow = {
  daysAhead?: number | null;
  time?: string | null;
} | null;

type BookingRuleCountLimit = {
  countLimit: number;
  countUnit?: "HOURS" | "BOOKINGS" | "ROUNDS" | null;
  qualifier?: string | null;
  timeLimit?: number | null;
  timeUnit?: "HOURS" | "DAYS" | "MONTHS" | "WEEKS" | "YEARS" | null;
  showNonViolating?: boolean | null;
} | null;

type BookingRuleSlotCriteria = {
  allDay?: boolean | null;
  startAt?: string | null;
  endAt?: string | null;
  courses?: BookingRuleCoursesConnection;
} | null;

type BookingRuleLike = {
  id?: string | null;
  __typename?: string | null;
  name?: string | null;
  displayName?: string | null;
  memberMessage?: string | null;
  bookingType?: "STANDARD" | "LESSON" | "DINING" | null;
  memberVisibility?:
    | "NONE"
    | "RESTRICTION_ONLY"
    | "SLOTS_AND_RESTRICTION"
    | "SLOTS_AND_RESTRICTION_INLINE"
    | "SLOTS_ONLY"
    | null;
  visibilityColor?: string | null;
  memberOverride?: "WARN" | "NONE" | null;
  staffOverride?: "FULL" | "WARN" | "NONE" | null;
  showNonViolating?: boolean | null;
  countLimit?: BookingRuleCountLimit;
  minPlayers?: number | null;
  maxPlayers?: number | null;
  liftBookingRuleWindow?: BookingRuleWindow;
  slotCriteria?: BookingRuleSlotCriteria;
};

export type NormalizedBookingRule = {
  id: string;
  __typename?: string | null;
  name: string;
  displayName: string | null;
  memberMessage: string | null;
  bookingType?: "STANDARD" | "LESSON" | "DINING" | null;
  visibility?: BookingRuleLike["memberVisibility"];
  visibilityColor?: string | null;
  memberOverride?: BookingRuleLike["memberOverride"];
  staffOverride?: BookingRuleLike["staffOverride"];
  showNonViolating?: boolean | null;
  countLimit?: BookingRuleCountLimit;
  minPlayers?: number | null;
  maxPlayers?: number | null;
  allDay: boolean;
  startAt: string | null;
  endAt: string | null;
  liftBookingRuleWindow?: BookingRuleWindow;
  courses: {
    count: number;
    edges: BookingRuleCourseEdge[];
  };
};

export const normalizeBookingRule = (
  rule: BookingRuleLike | null | undefined
): NormalizedBookingRule | null => {
  if (!rule?.id || !rule.name) {
    return null;
  }

  const courses = rule.slotCriteria?.courses;

  return {
    id: rule.id,
    __typename: rule.__typename,
    name: rule.name,
    displayName: rule.displayName ?? null,
    memberMessage: rule.memberMessage ?? null,
    bookingType: rule.bookingType,
    visibility: rule.memberVisibility ?? null,
    visibilityColor: rule.visibilityColor ?? null,
    memberOverride: rule.memberOverride ?? null,
    staffOverride: rule.staffOverride ?? null,
    showNonViolating: rule.showNonViolating ?? null,
    countLimit: rule.countLimit ?? null,
    minPlayers: rule.minPlayers ?? null,
    maxPlayers: rule.maxPlayers ?? null,
    allDay: rule.slotCriteria?.allDay ?? false,
    startAt: rule.slotCriteria?.startAt ?? null,
    endAt: rule.slotCriteria?.endAt ?? null,
    liftBookingRuleWindow: rule.liftBookingRuleWindow ?? null,
    courses: {
      count: courses?.count ?? 0,
      edges: [...(courses?.edges ?? [])],
    },
  };
};

export const normalizeBookingRules = <TRule extends BookingRuleLike>(
  rules: readonly (TRule | null | undefined)[] | null | undefined
) => {
  return (rules ?? []).flatMap((rule) => {
    const normalizedRule = normalizeBookingRule(rule);
    return normalizedRule ? [normalizedRule] : [];
  });
};
